import { useCallback, useEffect, useRef, useState } from "react";

type ElementReference = (node: HTMLDivElement | null) => void;

export interface ScrollFadeState {
  /** Attach to the scrollable element whose edges should be tracked (used as the IntersectionObserver root). */
  scrollReference: ElementReference;
  /** Attach to a zero-width sentinel rendered as the scrollable element's first child. */
  startSentinelReference: ElementReference;
  /** Attach to a zero-width sentinel rendered as the scrollable element's last child. */
  endSentinelReference: ElementReference;
  /** True while the start sentinel has scrolled out of view, i.e. content is hidden to the left/top. */
  showStartFade: boolean;
  /** True while the end sentinel has not yet scrolled into view, i.e. content is hidden to the right/bottom. */
  showEndFade: boolean;
}

/**
 * Tracks whether a horizontally-scrollable element currently has hidden
 * content at its start and/or end edge, so a caller can render a fade/shadow
 * affordance only where it's actually needed (and hide it once the user has
 * scrolled that edge fully into view). Driven by an IntersectionObserver
 * (rather than a scroll listener) watching zero-width sentinels placed at
 * either end of the scrollable content.
 *
 * The three elements are wired up via callback refs rather than `useRef`
 * objects: the scrollable element is commonly inside content that mounts
 * later than the hook itself (e.g. a dialog/sheet that starts closed), so a
 * one-time mount effect would find every ref still null and never observe
 * anything. Callback refs re-fire — and re-attach the observer — every time
 * the element actually enters or leaves the DOM.
 * @returns {ScrollFadeState} Callback refs for the scrollable element and its two edge sentinels, plus the current start/end fade visibility.
 */
export function useScrollFade(): ScrollFadeState {
  const rootReference = useRef<HTMLDivElement | null>(null);
  const startReference = useRef<HTMLDivElement | null>(null);
  const endReference = useRef<HTMLDivElement | null>(null);
  const observerReference = useRef<IntersectionObserver | null>(null);
  const [showStartFade, setShowStartFade] = useState(false);
  const [showEndFade, setShowEndFade] = useState(false);

  const attachObserverIfReady = useCallback(() => {
    observerReference.current?.disconnect();
    // eslint-disable-next-line unicorn/no-null -- IntersectionObserver refs are nullable by convention, matching useRef<T | null>
    observerReference.current = null;

    const root = rootReference.current;
    const startSentinel = startReference.current;
    const endSentinel = endReference.current;
    if (!root || !startSentinel || !endSentinel) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.target === startSentinel) {
            setShowStartFade(!entry.isIntersecting);
          } else if (entry.target === endSentinel) {
            setShowEndFade(!entry.isIntersecting);
          }
        }
      },
      { root, threshold: 1 }
    );

    observer.observe(startSentinel);
    observer.observe(endSentinel);
    observerReference.current = observer;
  }, []);

  const scrollReference = useCallback<ElementReference>(
    (node) => {
      rootReference.current = node;
      attachObserverIfReady();
    },
    [attachObserverIfReady]
  );

  const startSentinelReference = useCallback<ElementReference>(
    (node) => {
      startReference.current = node;
      attachObserverIfReady();
    },
    [attachObserverIfReady]
  );

  const endSentinelReference = useCallback<ElementReference>(
    (node) => {
      endReference.current = node;
      attachObserverIfReady();
    },
    [attachObserverIfReady]
  );

  useEffect(() => () => observerReference.current?.disconnect(), []);

  return { scrollReference, startSentinelReference, endSentinelReference, showStartFade, showEndFade };
}
